import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./FiltersBar.module.css";
import MultiSelect from "./MultiSelect";
import DateRange from "./DateRange";
import { SelectOption } from "../types";

export interface FiltersBarCompanyFilter {
    label?: string;
    placeholder: string;
    options: SelectOption[];
    selectedIds: string[];
    onSelectedIdsChange: (next: string[]) => void;
    loading?: boolean;
}

interface FiltersBarProps {
    selectorLabel?: string;
    selectorPlaceholder: string;
    selectorIcon?: React.ReactNode;
    options: SelectOption[];
    selectedIds: string[];
    onSelectedIdsChange: (next: string[]) => void;
    optionsLoading?: boolean;

    /** Owner-only: company multiselect to the left of the main selector. Empty = all companies. */
    companyFilter?: FiltersBarCompanyFilter | null;
    /** Optional one-line hint under the selector row (e.g. empty main list = all in scope). */
    filtersHint?: string;

    startDate: Date | null;
    endDate: Date | null;
    onStartDateChange: (d: Date | null) => void;
    onEndDateChange: (d: Date | null) => void;
    maxRangeDays?: number;

    canApply: boolean;
    applying: boolean;
    onApply: () => void;
    onReset: () => void;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
    selectorLabel,
    selectorPlaceholder,
    selectorIcon,
    options,
    selectedIds,
    onSelectedIdsChange,
    optionsLoading,
    companyFilter,
    filtersHint,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    maxRangeDays,
    canApply,
    applying,
    onApply,
    onReset,
}) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;

    return (
        <section className={styles.card} aria-label={tt("filtersAnalytics")}>
            <div className={styles.header}>
                <h3 className={styles.title}>{tt("filtersAnalytics")}</h3>
                <p className={styles.hint}>{tt("multipleSelectionAvailable")}</p>
            </div>
            <div className={styles.fields}>
                <div className={companyFilter ? styles.leftColumn : styles.mainOnly}>
                    {companyFilter ? (
                        <div className={styles.selectorPair}>
                            <MultiSelect
                                label={companyFilter.label}
                                placeholder={companyFilter.placeholder}
                                options={companyFilter.options}
                                value={companyFilter.selectedIds}
                                onChange={companyFilter.onSelectedIdsChange}
                                loading={companyFilter.loading}
                            />
                            <MultiSelect
                                label={selectorLabel}
                                placeholder={selectorPlaceholder}
                                options={options}
                                value={selectedIds}
                                onChange={onSelectedIdsChange}
                                loading={optionsLoading}
                                icon={selectorIcon}
                            />
                        </div>
                    ) : (
                        <MultiSelect
                            label={selectorLabel}
                            placeholder={selectorPlaceholder}
                            options={options}
                            value={selectedIds}
                            onChange={onSelectedIdsChange}
                            loading={optionsLoading}
                            icon={selectorIcon}
                        />
                    )}
                    {filtersHint ? <p className={styles.filtersHint}>{filtersHint}</p> : null}
                </div>
                <div className={styles.dateField}>
                    <DateRange
                        start={startDate}
                        end={endDate}
                        onStartChange={onStartDateChange}
                        onEndChange={onEndDateChange}
                        maxRangeDays={maxRangeDays}
                    />
                </div>
            </div>
            <div className={styles.actions}>
                <button type="button" className={styles.applyButton} disabled={!canApply || applying} onClick={onApply}>
                    {applying ? tt("loading") : tt("apply")}
                </button>
                <button type="button" className={styles.resetButton} onClick={onReset}>
                    {tt("reset")}
                </button>
            </div>
        </section>
    );
};

export default FiltersBar;
